import { Injectable } from '@angular/core';
import {
  StampDenominationRequest,
  StampDenominationRequestWithoutAccount,
} from './estamp.service';

export interface GroupedStampDenomination {
  account_id?: number;
  branch_code: string;
  article_code: string;
  total_stamp_duty: number;
  rowNumbers: number[];
}

export interface GroupingOptions {
  includeAccountId: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StampDenominationService {
  groupStampDenomination(
    excelData: any[],
    options: GroupingOptions,
  ): GroupedStampDenomination[] {
    const groupMap = new Map<string, GroupedStampDenomination>();

    excelData.forEach((row, index) => {
      if (
        !row.branch_code ||
        !row.article_code ||
        !row.stamp_duty_amount ||
        (options.includeAccountId && !row.account_id)
      ) {
        return;
      }

      const key = options.includeAccountId
        ? `${row.account_id}|${row.branch_code}|${row.article_code}`
        : `${row.branch_code}|${row.article_code}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          account_id: options.includeAccountId ? row.account_id : undefined,
          branch_code: row.branch_code,
          article_code: row.article_code,
          total_stamp_duty: 0,
          rowNumbers: [],
        });
      }

      const group = groupMap.get(key)!;
      group.total_stamp_duty += Number(row.stamp_duty_amount);
      group.rowNumbers.push(index + 2);
    });

    return Array.from(groupMap.values());
  }


  handleStampDenominationErrors(
    response: any,
    groupedData: GroupedStampDenomination[],
    convertedData: any[],
    issueList: any[],
    rowsWithIssues: any[],
    includeAccountId: boolean,
  ) {
    const errors = response?.content?.errors;
    if (!Array.isArray(errors) || errors.length === 0) return;

    const normalize = (v: any) => v?.toString().trim();

    for (const errorItem of errors) {
      const match = groupedData.find((g) => {
        const basicMatch =
          normalize(g.branch_code) === normalize(errorItem.branch_code) &&
          normalize(g.article_code) === normalize(errorItem.article_code);

        if (!basicMatch) return false;

        if (includeAccountId) {
          return normalize(g.account_id) === normalize(errorItem.account_id);
        }

        return true;
      });

      if (!match) continue;

      match.rowNumbers.forEach((rowNumber) => {
        issueList.push({
          rowNumber,
          error: `Insufficient stamp balance for account id ${errorItem.account_id}, ${errorItem.article_code}.
Required: ${errorItem.required_amount}, Available: ${errorItem.available_amount}`,
          errorType: 'STAMP_DENOMINATION',
        });

        const rowData = convertedData[rowNumber - 2];
        if (rowData && !rowsWithIssues.includes(rowData)) {
          rowsWithIssues.push(rowData);
        }
      });
    }
  }

  mapWithoutAccountId(
    groupedData: GroupedStampDenomination[],
  ): StampDenominationRequestWithoutAccount[] {
    return groupedData.map((g) => ({
      branch_code: g.branch_code,
      article_code: g.article_code,
      total_stamp_duty: g.total_stamp_duty,
    }));
  }

  mapToStampDenominationRequest(
    groupedData: GroupedStampDenomination[],
  ): StampDenominationRequest[] {
    return groupedData.map((g) => ({
      account_id: g.account_id, // optional
      branch_code: g.branch_code,
      article_code: g.article_code,
      total_stamp_duty: g.total_stamp_duty,
    }));
  }
}
